import { ChakraProvider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';
import 'react-quill-new/dist/quill.snow.css';

import { OpenAPI } from './client';
import { ColorModeProvider } from './components/ui/color-mode';
import './i18n';
import { LanguageProvider } from './providers/language-provider';
import { routeTree } from './routeTree.gen';
import { system } from './theme';

OpenAPI.BASE = import.meta.env.VITE_API_URL;

OpenAPI.TOKEN = async () => {
  return userManager
    .getUser()
    .then((user) => {
      return user?.access_token || '';
    })
    .catch(() => '');
};

const queryClient = new QueryClient();

const router = createRouter({ routeTree });
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

let rootOptions = {};

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [OpenAPI.BASE],
  });
  rootOptions['onUncaughtError'] = Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  });
  rootOptions['onCaughtError'] = Sentry.reactErrorHandler();
  rootOptions['onRecoverableError'] = Sentry.reactErrorHandler();
}

export const userManager = new UserManager({
  authority: import.meta.env.VITE_AUTHORITY,
  client_id: import.meta.env.VITE_CLIENT_ID,
  redirect_uri: `${window.location.origin}${window.location.pathname}`,
  post_logout_redirect_uri: window.location.origin,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  monitorSession: true,
});

export const onSigninCallback = () => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

ReactDOM.createRoot(document.getElementById('root')!, rootOptions).render(
  <StrictMode>
    <ChakraProvider value={system}>
      <ColorModeProvider forcedTheme="light">
        <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
          <LanguageProvider userManager={userManager}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </LanguageProvider>
        </AuthProvider>
      </ColorModeProvider>
    </ChakraProvider>
  </StrictMode>
);
